import { arrayExclude } from '../../helpers/array-utils';
import { BiMonoPredicate } from '../../interfaces/functions.interface';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { IResolvedTenor, ITenorRequest } from '../../interfaces';
import { isNil } from '@alk/core';
import { MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';

/**
 * Utils function to check if two ResolvedTenor have the same identity.
 * To be true, the Tenor and the Instrument must be the same. The Date
 * doesn't belong to the ResolvedTenor identity.
 */
export const isSameResolvedTenorIdentity = (tenor1: IResolvedTenor) => (tenor2: IResolvedTenor): boolean => {
    return !isNil(tenor1) &&
        !isNil(tenor2) &&
        tenor1.instrument === tenor2.instrument &&
        tenor1.tenor === tenor2.tenor;
};

export const resolvedTenorComparer: BiMonoPredicate<IResolvedTenor> =
    (item01: IResolvedTenor, item02: IResolvedTenor): boolean => {
        return isSameResolvedTenorIdentity(item01)(item02);
    };

export const resolvedToRequestedTenorComparer = (tenor: ITenorRequest) => (resolvedTenor: IResolvedTenor): boolean => {
    return isSameResolvedTenorIdentity(tenor as IResolvedTenor)(resolvedTenor);
};

export function keepRequestedTenors(
    requestedTenors: Array<ITenorRequest>,
): MonoTypeOperatorFunction<Array<IResolvedTenor>> {
    return pipe(
        map(resolvedTenors => {
            return resolvedTenors.filter(tenor => {
                return requestedTenors.find(isSameResolvedTenorIdentity(tenor));
            });
        }),
        distinctUntilChanged((oldTenors, newTenors) => {
            return oldTenors.length === newTenors.length &&
                oldTenors.every(oldTenor => {
                    const newTenorFound = newTenors.find(isSameResolvedTenorIdentity(oldTenor));

                    return newTenorFound.date === oldTenor.date;
                });
        }),
    );
}

export function assignPredefinedFlagFromTenorRequest(
    accumulatedResolveRequest$: Observable<Array<ITenorRequest>>,
): MonoTypeOperatorFunction<Array<IResolvedTenor>> {
    return pipe(
        withLatestFrom(accumulatedResolveRequest$),
        map(([ resolvedTenors, requestedTenors ]) => {
            return resolvedTenors.map(tenor => {
                const requested = requestedTenors.find(isSameResolvedTenorIdentity(tenor));

                return {
                    ...tenor,
                    isPredefined: requested?.isPredefined,
                };
            });
        }),
    );
}

export function excludeAlreadyRequestedTenors(
    accumulatedResolveRequest$: Observable<Array<ITenorRequest>>,
): MonoTypeOperatorFunction<Array<ITenorRequest>> {
    return pipe(
        withLatestFrom(accumulatedResolveRequest$),
        map(([ requestedTenors, resolvedTenors ]) => {
            return arrayExclude<ITenorRequest>(resolvedTenorComparer)(requestedTenors)(resolvedTenors);
        }),
    );
}
